'use client';

import { Award, Globe2, Users, Sparkles } from 'lucide-react';

const VALUES = [
  {
    icon: Award,
    title: 'Excellence',
    text: "Nous ne livrons que des pièces dont nous sommes fiers. Si une couture nous déplaît, nous la défaisons. Si un cuir ne nous séduit pas, nous le refusons. L'excellence n'est pas un objectif : c'est notre point de départ.",
  },
  {
    icon: Globe2,
    title: 'Pan Africanité',
    text: "Notre savoir-faire est né au Sénégal, affiné en Europe, partagé entre deux mondes. Nous refusons de choisir entre Dakar et Anvers : nous sommes les deux à la fois. Le cuir panafricain n'est pas un slogan, c'est notre ADN.",
  },
  {
    icon: Users,
    title: 'Héritage humain',
    text: "Chaque pièce porte le nom de l'artisan qui l'a façonnée. Pas une chaîne de montage anonyme : un maître, un apprenti, un dialogue. La main humaine ne se remplace pas, elle se transmet. C'est notre plus grande richesse.",
  },
  {
    icon: Sparkles,
    title: 'Patience',
    text: "42 jours de tannage, 36 heures pour un sac, 12 jours pour une pièce sur mesure. Le temps est notre allié, jamais notre ennemi. Ce que la hâte détruit, la patience l'embellit. Nous prenons le temps qu'il faut.",
  },
];

export default function AboutPage() {
  return (
    <section
      id="a-propos"
      className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden"
    >
      {/* Hero portrait */}
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 mb-16">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
          {/* Image — 2 cols */}
          <div className="lg:col-span-2 relative">
            <div className="relative aspect-[3/4] overflow-hidden border-gold-thin glow-gold">
              <img
                src="/images/matar-portrait.png"
                alt="S.E. MATAR, fondateur de la maison"
                className="w-full h-full object-cover"
                style={{ filter: 'brightness(0.95) saturate(1.05)' }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(180deg, transparent 60%, rgba(10,10,15,0.7) 100%)',
                }}
              />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="font-display text-xl text-[#F4E8C1] font-semibold tracking-wide">
                  S.E. MATAR
                </div>
                <div className="text-[#D4AF37] text-sm italic font-serif mt-1">
                  Fondateur & Maître Artisan
                </div>
              </div>
            </div>

            {/* Corner accents */}
            <div className="absolute -top-3 -right-3 w-20 h-20 border-t border-r border-[#D4AF37]/60 pointer-events-none" />
            <div className="absolute -bottom-3 -left-3 w-20 h-20 border-b border-l border-[#D4AF37]/60 pointer-events-none" />
          </div>

          {/* Text — 3 cols */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-5">
              <span className="block h-px w-10 bg-[#D4AF37]" />
              <span className="eyebrow">À Propos de la Maison</span>
            </div>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              <span className="text-gold-gradient">Une maison née</span>
              <br />
              <span className="text-[#F4E8C1]">de deux rives.</span>
            </h2>

            <div className="space-y-5 text-[#F4E8C1]/75 leading-relaxed font-light">
              <p>
                MATAR STYLE CUIR n'est pas une marque. C'est l'histoire d'un
                homme, S.E. MATAR, qui a grandi à Dakar en regardant son père
                coudre le cuir à la lueur d'une lampe à huile, et qui a traversé
                la Méditerranée pour porter ce savoir-faire jusqu'à Anvers.
              </p>
              <p>
                Fondée en 2014, la maison perpétue une double tradition : celle
                des selliers touaregs du Sahel, qui connaissent le cuir comme on
                connaît un ami, et celle des ateliers parisiens et bruxellois
                où la précision est reine. Entre ces deux héritages, MATAR a
                inventé sa grammaire : des lignes épurées, des cuirs patinés à
                la cire d'abeille, des pièces numérotées qui se transmettent.
              </p>
              <p>
                Aujourd'hui, l'atelier d'Anvers façonne pour le monde entier.
                Dix mille clients confiants dans quarante-deux pays. Une
                boutique sœur à Paris (ASMAO). Une usine solaire au Sénégal.
                Et la même obsession : le cuir comme art de vivre, le temps
                comme allié, la main humaine comme signature.
              </p>
            </div>

            {/* Manifesto quote */}
            <div className="mt-8 pl-5 border-l-2 border-[#D4AF37]/60">
              <p className="font-serif italic text-lg sm:text-xl text-[#F4E8C1]/85 leading-relaxed mb-3">
                « Je ne fabrique pas des sacs. Je façonne des objets qui
                accompagneront mes clients plus longtemps que je ne vivrai
                moi-même. »
              </p>
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
                — S.E. MATAR
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Values grid */}
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow">Nos Valeurs</span>
            <span className="block h-px w-10 bg-[#D4AF37]" />
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight">
            <span className="text-[#F4E8C1]">Quatre piliers,</span>{' '}
            <span className="text-gold-gradient">une seule obsession.</span>
          </h3>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {VALUES.map((v) => (
            <div
              key={v.title}
              className="group bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-6 hover:border-[#D4AF37]/50 transition-colors duration-400"
            >
              <div className="w-12 h-12 mb-4 rounded-full border border-[#D4AF37]/40 flex items-center justify-center group-hover:bg-gold-gradient transition-colors">
                <v.icon size={20} className="text-[#D4AF37] group-hover:text-[#0a0a0f] transition-colors" />
              </div>
              <h4 className="font-display text-base font-semibold text-[#F4E8C1] mb-3 tracking-wide uppercase">
                {v.title}
              </h4>
              <p className="text-xs text-[#F4E8C1]/65 leading-relaxed font-light">
                {v.text}
              </p>
            </div>
          ))}
        </div>

        {/* Final CTA */}
        <div className="mt-16 text-center bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-8 sm:p-10">
          <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#F4E8C1] mb-3 tracking-wide">
            Venez nous rencontrer à Anvers
          </h3>
          <p className="text-sm text-[#F4E8C1]/65 mb-6 max-w-md mx-auto font-light leading-relaxed">
            Le maître artisan vous reçoit sur rendez-vous. Une heure de visite
            privée, l'atelier ouvert, le cuir sous les doigts.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center justify-center gap-3 px-8 py-3.5 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.18em] text-xs hover:glow-gold transition-all duration-400"
          >
            Demander une visite privée
          </a>
        </div>
      </div>
    </section>
  );
}
